/**
 * useWeather — shared live weather hook backed by Open-Meteo (no API key).
 * Used by both UsefulInfoSidebar (widget) and WeatherPlaceholderView (full view)
 * so they always show the same data.
 *
 * A module-level cache means the API is only called once per session, and any
 * component that mounts later receives the cached result immediately.
 */

import { useState, useEffect, useCallback } from 'react';

// ---------------------------------------------------------------------------
// WMO weather code → emoji + simplified label
// https://open-meteo.com/en/docs#weathervariables
// ---------------------------------------------------------------------------
export function wmoToLabel(code: number): { icon: string; label: string; simple: 'sunny' | 'partly-cloudy' | 'cloudy' | 'rainy' } {
  if (code === 0)  return { icon: '☀️',  label: 'Clear Sky',      simple: 'sunny' };
  if (code <= 2)   return { icon: '🌤️',  label: 'Partly Cloudy',  simple: 'partly-cloudy' };
  if (code === 3)  return { icon: '☁️',  label: 'Overcast',       simple: 'cloudy' };
  if (code <= 49)  return { icon: '🌫️',  label: 'Foggy',          simple: 'cloudy' };
  if (code <= 59)  return { icon: '🌦️',  label: 'Drizzle',        simple: 'rainy' };
  if (code <= 69)  return { icon: '🌧️',  label: 'Rain',           simple: 'rainy' };
  if (code <= 79)  return { icon: '🌨️',  label: 'Snow',           simple: 'rainy' };
  if (code <= 84)  return { icon: '🌧️',  label: 'Rain Showers',   simple: 'rainy' };
  if (code <= 86)  return { icon: '🌨️',  label: 'Snow Showers',   simple: 'rainy' };
  if (code <= 99)  return { icon: '⛈️',  label: 'Thunderstorm',   simple: 'rainy' };
  return           { icon: '🌡️',         label: 'Unknown',        simple: 'partly-cloudy' };
}

const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface LiveWeather {
  location: string;
  current: {
    temp: number;
    condition: string;
    simple: 'sunny' | 'partly-cloudy' | 'cloudy' | 'rainy';
    icon: string;
    humidity: number;
    wind: number;
  };
  forecast: { day: string; high: number; low: number; icon: string; condition: string; precipitation: number; wind: number; uvIndex: number }[];
}

// ---------------------------------------------------------------------------
// Module-level cache — survives across component mount/unmount cycles
// ---------------------------------------------------------------------------
let _cache: LiveWeather | null = null;
let _error: string | null = null;
let _fetching = false;
let _version = 0; // bumped on every state change so React re-renders
const _listeners = new Set<() => void>();

function notifyListeners(): void {
  _version++;
  _listeners.forEach(cb => cb());
}

function getEnv(key: string, fallback: string): string {
  const val = import.meta.env?.[key];
  return typeof val === 'string' && val.length > 0 ? val : fallback;
}

async function fetchWeather(): Promise<void> {
  if (_fetching) return;
  _fetching = true;
  _error = null;
  notifyListeners();

  const lat  = getEnv('VITE_WEATHER_LAT', '38.7223');
  const lon  = getEnv('VITE_WEATHER_LON', '-9.1393');
  const name = getEnv('VITE_WEATHER_LOCATION', 'Lisbon, Portugal');

  // Use current Open-Meteo parameter names (Nov 2024+)
  const url =
    `https://api.open-meteo.com/v1/forecast` +
    `?latitude=${lat}&longitude=${lon}` +
    `&current=temperature_2m,weather_code,wind_speed_10m,relative_humidity_2m` +
    `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,wind_speed_10m_max,uv_index_max` +
    `&timezone=auto&forecast_days=5`;

  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();

    // Validate response shape
    const cur = data?.current;
    const daily = data?.daily;
    if (
      !cur || typeof cur.temperature_2m !== 'number' ||
      !daily || !Array.isArray(daily.time) || !Array.isArray(daily.weather_code)
    ) {
      throw new Error('Unexpected API response shape');
    }

    const curW = wmoToLabel(cur.weather_code);

    const forecast = (daily.time as string[]).map((dateStr: string, i: number) => {
      const date = new Date(dateStr + 'T12:00:00');
      const dayLabel =
        i === 0 ? 'Today' :
        i === 1 ? 'Tomorrow' :
        (DAYS_SHORT[date.getDay()] ?? date.toLocaleDateString('en-US', { weekday: 'short' }));
      const { icon, label } = wmoToLabel(daily.weather_code[i]);
      return {
        day: dayLabel,
        high: Math.round(daily.temperature_2m_max[i]),
        low:  Math.round(daily.temperature_2m_min[i]),
        icon,
        condition: label,
        precipitation: Math.round((daily.precipitation_sum?.[i] ?? 0) * 10) / 10,
        wind: Math.round(daily.wind_speed_10m_max?.[i] ?? 0),
        uvIndex: Math.round(daily.uv_index_max?.[i] ?? 0),
      };
    });

    _cache = {
      location: name,
      current: {
        temp:      Math.round(cur.temperature_2m),
        condition: curW.label,
        simple:    curW.simple,
        icon:      curW.icon,
        humidity:  cur.relative_humidity_2m,
        wind:      Math.round(cur.wind_speed_10m),
      },
      forecast,
    };
    _error = null;
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error';
    console.error('[useWeather] fetch failed:', msg);
    _error = msg;
  } finally {
    _fetching = false;
    notifyListeners();
  }
}

// Kick off the initial fetch at import time so data is ready ASAP
fetchWeather();

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useWeather() {
  const [, setTick] = useState(_version);

  useEffect(() => {
    // Subscribe to module-level state changes
    const handler = () => setTick(_version);
    _listeners.add(handler);

    // If no data and not fetching (e.g., previous error), retry
    if (!_cache && !_fetching) fetchWeather();

    return () => { _listeners.delete(handler); };
  }, []);

  const refresh = useCallback(() => {
    _cache = null;
    _error = null;
    _fetching = false; // reset so fetchWeather() doesn't bail
    fetchWeather();
  }, []);

  return {
    weather: _cache,
    loading: _fetching,
    error: _error,
    refresh,
  };
}
